import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Sparkles } from "lucide-react";

export default function BecomeAffiliatePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: user?.email || "",
    handle: "",
    audience_size: "",
    destinations: "",
    payout_method: "USDT",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      // Re-use waitlist endpoint with a special slug for now (MVP)
      await api.post("/waitlist", {
        email: form.email,
        name: form.name || undefined,
        destination_slug: "affiliate-program",
        source: `affiliate · handle:${form.handle} · audience:${form.audience_size} · payout:${form.payout_method} · destinations:${form.destinations} · notes:${form.notes}`,
      });
      setDone(true);
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="pt-32 pb-24 px-5 md:px-10 mx-auto max-w-6xl">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="eyebrow mb-3">For affiliates &amp; creators</div>
          <h1 className="section-title text-5xl md:text-6xl mb-6">Earn by promoting premium experiences.</h1>
          <p className="text-muted leading-relaxed">Crypto-paid commissions on confirmed bookings, with attribution starting from your very first link.</p>
          <div className="mt-10 space-y-6">
            {[
              { t: "Crypto payouts", d: "USDT or USDC, monthly, no minimum threshold." },
              { t: "Real attribution", d: "UTM, referral codes, and partner IDs tracked from day one." },
              { t: "Curated campaigns", d: "Last-minute yachts, VIP tables, weekend villas — promote what converts." },
              { t: "Premium audience", d: "Reach the Meet & Mint network of crypto-native travellers." },
            ].map((b) => (
              <div key={b.t}>
                <div className="font-serif-display text-2xl text-bone">{b.t}</div>
                <div className="text-sm text-muted">{b.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          {done ? (
            <div className="luxe-card p-10 text-center">
              <Sparkles className="mx-auto text-teal mb-3"/>
              <div className="font-script text-4xl text-bone mb-2">You're on the list.</div>
              <p className="text-sm text-muted">We'll email you the moment our affiliate dashboard opens.</p>
              <button onClick={() => navigate("/")} className="btn btn-primary mt-8" data-testid="ba-go-home">Back to home</button>
            </div>
          ) : (
            <form onSubmit={submit} className="luxe-card p-7 space-y-4" data-testid="affiliate-form">
              <h2 className="font-serif-display text-3xl text-bone mb-2">Apply to become an affiliate</h2>
              <input required placeholder="Full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input-bare" data-testid="ba-name"/>
              <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input-bare" data-testid="ba-email"/>
              <input placeholder="Social handle (Instagram, X, TikTok…)" value={form.handle} onChange={e => setForm({...form, handle: e.target.value})} className="input-bare" data-testid="ba-handle"/>
              <input placeholder="Audience size (e.g. 80k followers, 500k monthly views)" value={form.audience_size} onChange={e => setForm({...form, audience_size: e.target.value})} className="input-bare" data-testid="ba-audience"/>
              <input placeholder="Destinations of interest (Ibiza, Mykonos…)" value={form.destinations} onChange={e => setForm({...form, destinations: e.target.value})} className="input-bare"/>
              <select className="input-bare" value={form.payout_method} onChange={e => setForm({...form, payout_method: e.target.value})}>
                <option value="USDT">Payout — USDT</option>
                <option value="USDC">Payout — USDC</option>
                <option value="EUR">Payout — EUR (bank)</option>
              </select>
              <textarea placeholder="Anything else we should know?" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input-bare"/>
              {error && <div className="text-rose-300 text-sm">{error}</div>}
              <button type="submit" disabled={busy} className="btn btn-primary w-full" data-testid="ba-submit">
                {busy ? "Sending…" : "Submit application"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
